/**
 * The journal — and the answer to "there is no profile of the user, so how
 * does the profile grow?"
 *
 * Astrally is private by default: with the social layer OFF there is no feed,
 * no matches and nobody to look at. What there IS, and what has to be worth
 * opening every day, is the reader's own record — what they wrote, what they
 * lived, what they kept, and how the readings actually landed. This screen is
 * that record, and it is deliberately the only place in the app where a number
 * goes UP over time.
 *
 * Three views, one screen. They are tabs rather than routes because they are
 * three angles on one dataset (entries + ratings) and because App.tsx belongs
 * to another agent — hooking up `/journal` needs exactly one route pointing
 * here, no sub-routes.
 *
 * Storage is local (see journalStore.ts). Journal bodies never go to matching,
 * and never to the AI unless the user attaches one themselves.
 */

import { useMemo, useState } from 'react';
import type { JournalEntry, JournalEntryKind } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { ScreenHeader, Section } from '../../components/common';
import { Term } from '../../components/Term';
import { AccuracyDashboard, SYSTEM_LABEL } from './AccuracyDashboard';
import { EntryEditor, KIND_LABEL, LIFE_EVENT_LABEL } from './EntryEditor';
import { TimelineCard, dashaLabelAt } from './TimelineCard';
import { decisiveOf, overallTally, useJournal, type JournalDraft } from './journalStore';

type View = 'journal' | 'accuracy' | 'timeline';

const VIEWS: { id: View; label: string }[] = [
  { id: 'journal', label: 'Journal' },
  { id: 'accuracy', label: 'Accuracy' },
  { id: 'timeline', label: 'Timeline' },
];

type KindFilter = JournalEntryKind | 'all';

const KIND_FILTERS: { id: KindFilter; label: string }[] = [
  { id: 'all', label: 'Everything' },
  { id: 'note', label: 'Notes' },
  { id: 'life-event', label: 'Life events' },
  { id: 'reading-saved', label: 'Saved readings' },
  { id: 'prediction-rated', label: 'Rated' },
];

const MOOD_GLYPH: Record<number, string> = { 1: '⛆', 2: '☁', 3: '◐', 4: '☼', 5: '✧' };

const MONTH_FMT = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
const DAY_FMT = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

export function JournalScreen() {
  const app = useApp();
  const journal = useJournal(app.uid);
  const [view, setView] = useState<View>('journal');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ entry?: JournalEntry; kind: JournalEntryKind } | null>(
    null,
  );

  const vimshottari = app.chart?.jyotish?.vimshottari;

  const allTags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of journal.entries) {
      for (const tag of e.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [journal.entries]);

  const filtered = useMemo(
    () =>
      journal.entries.filter(
        (e) =>
          (kindFilter === 'all' || e.kind === kindFilter) &&
          (tagFilter === null || e.tags.includes(tagFilter)),
      ),
    [journal.entries, kindFilter, tagFilter],
  );

  const saveDraft = async (draft: JournalDraft) => {
    if (editing?.entry) await journal.updateEntry(editing.entry.id, draft);
    else await journal.addEntry(draft);
  };

  return (
    <div className="screen">
      <ScreenHeader
        title="Your journal"
        subtitle="Private to this device. Never used for matching."
        back
        right={
          <button
            className="btn btn--primary btn--small"
            onClick={() => setEditing({ kind: 'note' })}
          >
            <span aria-hidden>＋</span> New
          </button>
        }
      />

      <ProgressStrip journal={journal} />

      <div className="seg" style={{ marginTop: 12 }}>
        {VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className={`seg__opt${view === v.id ? ' seg__opt--on' : ''}`}
            aria-pressed={view === v.id}
            onClick={() => setView(v.id)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {!journal.ready ? (
        <div className="row" style={{ justifyContent: 'center', padding: 40 }}>
          <span className="spinner" />
        </div>
      ) : view === 'accuracy' ? (
        <div style={{ marginTop: 14 }}>
          <AccuracyDashboard stats={journal.stats} daysTracked={journal.progress.daysTracked} />
        </div>
      ) : view === 'timeline' ? (
        <div style={{ marginTop: 14 }}>
          <TimelineCard entries={journal.entries} vimshottari={vimshottari} />
        </div>
      ) : journal.entries.length === 0 ? (
        <JournalEmptyState
          onStart={(kind) => setEditing({ kind })}
          dashaNow={vimshottari ? dashaLabelAt(vimshottari, Date.now()) : null}
        />
      ) : (
        <>
          <div className="wrap" style={{ marginTop: 14 }}>
            {KIND_FILTERS.map((f) => {
              const n =
                f.id === 'all'
                  ? journal.entries.length
                  : journal.entries.filter((e) => e.kind === f.id).length;
              if (n === 0 && f.id !== 'all') return null;
              return (
                <button
                  key={f.id}
                  type="button"
                  className={`chip${kindFilter === f.id ? ' chip--on' : ''}`}
                  aria-pressed={kindFilter === f.id}
                  style={{ minHeight: 36 }}
                  onClick={() => setKindFilter(f.id)}
                >
                  {f.label}
                  <span className="tiny faint">{n}</span>
                </button>
              );
            })}
          </div>

          {allTags.length > 0 && (
            <div className="wrap" style={{ marginTop: 8 }}>
              {allTags.slice(0, 12).map(([tag, n]) => (
                <button
                  key={tag}
                  type="button"
                  className={`chip${tagFilter === tag ? ' chip--on' : ''}`}
                  aria-pressed={tagFilter === tag}
                  style={{ minHeight: 32, fontSize: '0.75rem' }}
                  onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
                >
                  #{tag}
                  <span className="tiny faint">{n}</span>
                </button>
              ))}
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="card center muted small" style={{ marginTop: 14 }}>
              Nothing matches that filter yet.
              <div style={{ marginTop: 10 }}>
                <button
                  className="btn btn--ghost btn--small"
                  onClick={() => {
                    setKindFilter('all');
                    setTagFilter(null);
                  }}
                >
                  Clear filters
                </button>
              </div>
            </div>
          ) : (
            <EntryList
              entries={filtered}
              vimNow={vimshottari}
              onOpen={(entry) => setEditing({ entry, kind: entry.kind })}
            />
          )}
        </>
      )}

      {editing && (
        <EntryEditor
          entry={editing.entry}
          initialKind={editing.kind}
          onSave={saveDraft}
          onDelete={
            editing.entry ? () => journal.deleteEntry(editing.entry!.id) : undefined
          }
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progress — the profile, visibly growing
// ---------------------------------------------------------------------------

function ProgressStrip({ journal }: { journal: ReturnType<typeof useJournal> }) {
  const { progress, stats } = journal;
  const decisive = decisiveOf(overallTally(stats));

  /** One concrete next step, never a vague "keep going". */
  const milestone = (() => {
    if (progress.entries === 0) {
      return 'Your first entry starts the record. Everything below grows from it.';
    }
    if (decisive < 5) {
      const need = 5 - decisive;
      return `Grade ${need} more reading${need === 1 ? '' : 's'} and your first hit rate appears.`;
    }
    if (progress.lifeEvents < 5) {
      const need = 5 - progress.lifeEvents;
      return `${need} more dated life event${need === 1 ? '' : 's'} and your dasha timeline starts to show a shape.`;
    }
    if (progress.streakDays === 0) {
      return 'Rate one signal today to restart your streak.';
    }
    return `${progress.streakDays} day${progress.streakDays === 1 ? '' : 's'} in a row. This record is only as good as it is honest — miss days rather than fake them.`;
  })();

  return (
    <div className="card card--strong" style={{ padding: 14 }}>
      <div className="row" style={{ gap: 0 }}>
        <Metric value={progress.entries} label={progress.entries === 1 ? 'entry' : 'entries'} />
        <Metric value={progress.rated} label="rated" />
        <Metric value={progress.daysTracked} label="days" />
      </div>
      <div className="tiny muted" style={{ marginTop: 10, lineHeight: 1.5 }}>
        {milestone}
      </div>
    </div>
  );
}

function Metric({ value, label }: { value: number; label: string }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div style={{ fontSize: '1.45rem', fontWeight: 700, color: 'var(--accent-ink)' }}>{value}</div>
      <div className="tiny faint">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

function EntryList({
  entries,
  vimNow,
  onOpen,
}: {
  entries: JournalEntry[];
  vimNow: Parameters<typeof dashaLabelAt>[0] | undefined;
  onOpen: (entry: JournalEntry) => void;
}) {
  // Month headers make a long reverse-chronological list scannable without a
  // date on every row shouting the same month over and over.
  const groups = useMemo(() => {
    const out: { label: string; items: JournalEntry[] }[] = [];
    for (const entry of entries) {
      const d = new Date(entry.atISO);
      const label = Number.isNaN(d.getTime()) ? 'Undated' : MONTH_FMT.format(d);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(entry);
      else out.push({ label, items: [entry] });
    }
    return out;
  }, [entries]);

  return (
    <div>
      {groups.map((group) => (
        <Section key={group.label} title={group.label}>
          <div className="card" style={{ padding: '4px 14px' }}>
            {group.items.map((entry, i) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                vimNow={vimNow}
                last={i === group.items.length - 1}
                onOpen={() => onOpen(entry)}
              />
            ))}
          </div>
        </Section>
      ))}
    </div>
  );
}

function EntryRow({
  entry,
  vimNow,
  last,
  onOpen,
}: {
  entry: JournalEntry;
  vimNow: Parameters<typeof dashaLabelAt>[0] | undefined;
  last: boolean;
  onOpen: () => void;
}) {
  const d = new Date(entry.atISO);
  const dasha = vimNow ? dashaLabelAt(vimNow, d.getTime()) : null;
  return (
    <button
      type="button"
      className="spread"
      style={{
        width: '100%',
        textAlign: 'left',
        gap: 12,
        padding: '12px 0',
        border: 'none',
        borderBottom: last ? 'none' : '1px solid var(--border)',
        background: 'transparent',
        color: 'var(--text)',
        font: 'inherit',
        cursor: 'pointer',
        alignItems: 'flex-start',
      }}
      onClick={onOpen}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
          <span className="tiny faint">
            {Number.isNaN(d.getTime()) ? '—' : DAY_FMT.format(d)}
          </span>
          <span
            className="tiny"
            style={{ color: 'var(--text-faint)' }}
            data-mode={entry.linkedSystem}
          >
            · {KIND_LABEL[entry.kind]}
            {entry.linkedSystem ? ` · ${SYSTEM_LABEL[entry.linkedSystem]}` : ''}
          </span>
          {dasha && <span className="tiny faint">· {dasha}</span>}
        </div>
        <div style={{ fontWeight: 600, fontSize: '0.93rem', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
          {entry.title}
        </div>
        {entry.body && (
          <div
            className="tiny muted"
            style={{
              marginTop: 3,
              lineHeight: 1.45,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {entry.body}
          </div>
        )}
        {(entry.tags.length > 0 || entry.lifeEventType) && (
          <div className="wrap" style={{ marginTop: 6, gap: 6 }}>
            {entry.lifeEventType && (
              <span className="chip chip--fact" style={{ minHeight: 0 }}>
                {LIFE_EVENT_LABEL[entry.lifeEventType]}
              </span>
            )}
            {entry.tags.map((tag) => (
              <span
                key={tag}
                className="tiny faint"
                style={{
                  padding: '2px 8px',
                  borderRadius: 999,
                  border: '1px solid var(--border)',
                }}
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
      {entry.mood && (
        <span
          aria-label={`Mood ${entry.mood} of 5`}
          className="small"
          style={{ color: 'var(--gold-ink)', flexShrink: 0, paddingTop: 14 }}
        >
          {MOOD_GLYPH[entry.mood]}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Empty state — sells the feature
// ---------------------------------------------------------------------------

function JournalEmptyState({
  onStart,
  dashaNow,
}: {
  onStart: (kind: JournalEntryKind) => void;
  dashaNow: string | null;
}) {
  return (
    <div className="col" style={{ gap: 12, marginTop: 14 }}>
      <div className="card col" style={{ gap: 12 }}>
        <div className="h2">This is the part that becomes yours</div>
        <div className="small muted" style={{ lineHeight: 1.6 }}>
          A <Term id="kundali">kundli</Term> is fixed the moment you are born. Everything that makes
          Astrally about <em>you</em> rather than about a birth certificate gets written here — by
          you, over time.
        </div>

        <Sell
          glyph="✎"
          title="Write it down while it is happening"
          body="A line about the day, a mood, a tag. Two months later the pattern is visible in a way memory never manages."
        />
        <Sell
          glyph="◆"
          title="Log the dates that mattered"
          body={
            dashaNow
              ? `The move, the job, the break-up. Each one gets pinned to the dasha chapter that was running — you are in ${dashaNow} right now.`
              : 'The move, the job, the break-up. Each one gets pinned to the dasha chapter that was running at the time.'
          }
        />
        <Sell
          glyph="✓"
          title="Grade the readings"
          body="Landed, partly, missed. Do it for a few weeks and you get an honest answer to the only question that matters: does this actually read me?"
        />

        <button className="btn btn--primary btn--block" onClick={() => onStart('note')}>
          Write your first entry
        </button>
        <button className="btn btn--ghost btn--block" onClick={() => onStart('life-event')}>
          Or log something that already happened
        </button>
      </div>

      <div className="banner banner--info" style={{ lineHeight: 1.55 }}>
        Everything here stays on this device. It is never sent to matching, never shown to another
        person, and never given to the AI unless you attach an entry to a conversation yourself.
      </div>
    </div>
  );
}

function Sell({ glyph, title, body }: { glyph: string; title: string; body: string }) {
  return (
    <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
      <span
        aria-hidden
        style={{ color: 'var(--gold-ink)', fontSize: '0.95rem', lineHeight: 1.5, flexShrink: 0 }}
      >
        {glyph}
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="h3">{title}</div>
        <div className="tiny muted" style={{ lineHeight: 1.55, marginTop: 2 }}>
          {body}
        </div>
      </div>
    </div>
  );
}
